import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgSelectModule } from '@ng-select/ng-select';
import { HorizonLotEngagementComponent } from '@common/horizon-lot-engagement/horizon-lot-engagement.component';
import { HorizonTrackingService } from '@services/api/horizon-tracking.service';
import { shouldHideError } from '@services/helpers/server-error-silence';
import { Subscription, catchError, from, map, mergeMap, of } from 'rxjs';
import { HorizonMatrixHeatmap, HorizonMatrixLot, HorizonMatrixViewBy } from '../lead-horizon-tracking.model';

/** What colours a lot: the engagement state (lead tab) or the interest level (project report). */
export type HorizonHeatmapColorBy = 'engagement' | 'interest';

/** One level of the building: its own columns and lots, filled in as each level's request returns. */
export interface HorizonLevelSection {
  level: string;
  columns: string[];
  lots: HorizonMatrixLot[];
  isLoading: boolean;
  hasError: boolean;
}

const INTEREST_LEGEND = [
  { key: 'high', label: 'High Interest' },
  { key: 'medium', label: 'Medium Interest' },
  { key: 'low', label: 'Low Interest' },
  { key: 'none', label: 'No Activity' },
];

/** Zoom steps: 0 is the normal view; 1 and 2 shrink the tiles so every level fits without the inner scroll. */
const MAX_ZOOM_OUT = 2;

/** At most this many level requests run at once, so a tall building does not flood the API. */
const LEVEL_REQUESTS_AT_ONCE = 4;

/**
 * Project Heat Map.
 *
 * The project's own price list matrix, coloured by Horizon engagement — from one buyer when
 * `leadId` is set (lead tab), else from every buyer on the project (project report). Every level of the building is listed under its own heading inside one scrolling box,
 * so the levels can be read top to bottom, and the level list beside it jumps to a level. Availability
 * (Sold, Available…) is shown separately from the colour. Selecting a lot opens that lot's activity.
 */
@Component({
  selector: 'app-horizon-matrix-heatmap',
  standalone: true,
  imports: [CommonModule, FormsModule, NgSelectModule, HorizonLotEngagementComponent],
  templateUrl: './horizon-matrix-heatmap.component.html',
  styleUrls: ['../horizon-card.scss', './horizon-matrix-heatmap.component.scss'],
})
export class HorizonMatrixHeatmapComponent implements OnChanges, OnDestroy {
  @Input() projectId: string | null = null;
  /** Set once per load by the tab, so this panel follows the tab's date range. */
  @Input() range: { from?: string; to?: string } = {};
  @Input() colorBy: HorizonHeatmapColorBy = 'engagement';
  /** `viewer` limits the counts to what the signed-in user may see; the lead tab leaves it off for the whole project. */
  @Input() scope: 'viewer' | undefined;
  /** Set on the lead tab: the grid is coloured by this one buyer's activity, not every buyer's. */
  @Input() leadId: string | null = null;
  @Input() title = 'Project Heat Map';
  @Input() subtitle = 'Horizon activity from all buyers on this project.';

  @ViewChild('levelsBox') levelsBox: ElementRef<HTMLElement>;

  heatmap: HorizonMatrixHeatmap | null = null;
  sections: HorizonLevelSection[] = [];
  /**
   * Always buildings. A Remmi project is one building, so the backend's Buildings and Levels views
   * returned the same grid; the toggle was removed. Floor plans are filtered with the dropdown instead.
   */
  readonly viewBy: HorizonMatrixViewBy = 'buildings';
  buildingId: string | null = null;
  /** The level the list highlights: the one at the top of the scroll box. */
  level: string | null = null;
  floorplanId: string | null = null;
  openBuildingId: string | null = null;
  selectedLot: HorizonMatrixLot | null = null;
  isLoading = false;
  hasLoadError = false;
  /** 0 = normal tiles in a scrolling box; higher = zoomed out, every level shown at once. */
  zoom = 0;
  readonly maxZoomOut = MAX_ZOOM_OUT;

  private subscription: Subscription;
  private jumpingUntil = 0;
  private levelsSubscription: Subscription;

  constructor(
    private horizon: HorizonTrackingService,
    private router: Router
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['projectId'] || changes['range'] || changes['leadId']) && this.projectId) {
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.levelsSubscription?.unsubscribe();
  }

  // ---- Loading -----------------------------------------------------------------------------

  /** The first request returns the buildings and the lowest active level; the other levels are then fetched one by one. */
  load(): void {
    this.subscription?.unsubscribe();
    this.levelsSubscription?.unsubscribe();
    this.isLoading = true;
    this.hasLoadError = false;
    this.subscription = this.horizon.getMatrixHeatmap(this.projectId, this.range, this.query()).subscribe({
      next: res => {
        this.heatmap = res?.data?.available ? res.data : null;
        this.buildingId = this.heatmap?.selected?.building_id ?? null;
        this.openBuildingId = this.openBuildingId ?? this.buildingId;
        this.isLoading = false;
        this.buildSections();
      },
      error: () => {
        this.heatmap = null;
        this.sections = [];
        this.hasLoadError = !shouldHideError(null);
        this.isLoading = false;
      },
    });
  }

  private query(level?: string): { view_by: string; building_id?: string; level?: string; floorplan?: string; scope?: 'viewer'; lead_id?: string } {
    return {
      view_by: this.viewBy,
      building_id: this.buildingId ?? undefined,
      level,
      floorplan: this.floorplanId ?? undefined,
      scope: this.scope,
      lead_id: this.leadId ?? undefined,
    };
  }

  /** Every level of the selected building becomes a section; the level the first response drew is already filled. */
  private buildSections(): void {
    const heatmap = this.heatmap;
    const levels = heatmap?.buildings.find(building => building.building_id === this.buildingId)?.levels ?? [];
    const drawn = heatmap?.selected?.level;
    this.sections = levels.map(level =>
      level === drawn
        ? { level, columns: heatmap.columns, lots: heatmap.lots, isLoading: false, hasError: false }
        : { level, columns: [], lots: [], isLoading: true, hasError: false }
    );
    this.level = this.sections[0]?.level ?? null;
    this.loadOtherLevels(levels.filter(level => level !== drawn));
  }

  private loadOtherLevels(levels: string[]): void {
    this.levelsSubscription = from(levels)
      .pipe(
        mergeMap(
          level =>
            this.horizon.getMatrixHeatmap(this.projectId, this.range, this.query(level)).pipe(
              map(res => ({ level, data: res?.data, failed: false })),
              catchError(() => of({ level, data: null, failed: true }))
            ),
          LEVEL_REQUESTS_AT_ONCE
        )
      )
      .subscribe(({ level, data, failed }) => {
        const section = this.sections.find(item => item.level === level);
        if (!section) {
          return;
        }
        section.isLoading = false;
        section.hasError = failed;
        section.columns = data?.available ? data.columns : [];
        section.lots = data?.available ? data.lots : [];
      });
  }

  retryLevel(section: HorizonLevelSection): void {
    section.isLoading = true;
    section.hasError = false;
    this.horizon.getMatrixHeatmap(this.projectId, this.range, this.query(section.level)).subscribe({
      next: res => {
        section.isLoading = false;
        section.columns = res?.data?.available ? res.data.columns : [];
        section.lots = res?.data?.available ? res.data.lots : [];
      },
      error: () => {
        section.isLoading = false;
        section.hasError = true;
      },
    });
  }

  // ---- Controls --------------------------------------------------------------------------------

  get buildingName(): string {
    return this.heatmap?.buildings.find(building => building.building_id === this.buildingId)?.name ?? '';
  }

  setFloorplan(floorplanId: string | null): void {
    this.floorplanId = floorplanId;
    this.load();
  }

  toggleBuilding(buildingId: string): void {
    this.openBuildingId = this.openBuildingId === buildingId ? null : buildingId;
  }

  /**
   * Jumps the scroll box to a level's heading. Positions are measured on screen, not with
   * `offsetTop`, which counts from whatever ancestor is positioned and put the highlight on the
   * wrong level.
   */
  scrollToLevel(level: string): void {
    const box = this.levelsBox?.nativeElement;
    const block = this.levelBlocks(box).find(item => item.dataset['level'] === level);
    if (box && block) {
      this.level = level;
      // The smooth scroll fires scroll events on the way; keep the clicked level highlighted meanwhile.
      this.jumpingUntil = Date.now() + 800;
      const top = box.scrollTop + block.getBoundingClientRect().top - box.getBoundingClientRect().top;
      box.scrollTo({ top, behavior: 'smooth' });
    }
  }

  /** The level list follows the scroll: it highlights the level whose block is at the top of the box. */
  onLevelsScroll(): void {
    const box = this.levelsBox?.nativeElement;
    if (!box || Date.now() < this.jumpingUntil) {
      return;
    }
    const blocks = this.levelBlocks(box);
    const boxTop = box.getBoundingClientRect().top + 8;
    // At the very bottom the last levels can never reach the top, so the last one wins there.
    const isAtEnd = box.scrollTop + box.clientHeight >= box.scrollHeight - 2;
    const current = isAtEnd ? blocks[blocks.length - 1] : (blocks.find(block => block.getBoundingClientRect().bottom > boxTop) ?? blocks[0]);
    this.level = current?.dataset['level'] ?? this.level;
  }

  private levelBlocks(box: HTMLElement | undefined): HTMLElement[] {
    return Array.from(box?.querySelectorAll<HTMLElement>('[data-level]') ?? []);
  }

  // ---- Zoom ---------------------------------------------------------------------------------------

  get isZoomedOut(): boolean {
    return this.zoom > 0;
  }

  zoomOut(): void {
    this.zoom = Math.min(MAX_ZOOM_OUT, this.zoom + 1);
  }

  zoomIn(): void {
    this.zoom = Math.max(0, this.zoom - 1);
  }

  /** "Fit all levels" jumps straight to the smallest tiles; pressed again it returns to the normal view. */
  toggleFitAll(): void {
    this.zoom = this.isZoomedOut ? 0 : MAX_ZOOM_OUT;
  }

  // ---- Lots -----------------------------------------------------------------------------------------

  /** The legend follows what colours the tiles. */
  get legend(): { key: string; label: string }[] {
    return this.colorBy === 'interest' ? (this.heatmap?.interest_legend ?? INTEREST_LEGEND) : (this.heatmap?.legend ?? []);
  }

  /** The colour key of one lot: its interest level or its engagement state. Sold lots stay grey either way. */
  stateOf(lot: HorizonMatrixLot): string {
    if (this.isSold(lot)) {
      return 'sold';
    }
    return this.colorBy === 'interest' ? `interest-${lot.interest?.level ?? 'none'}` : lot.engagement.state;
  }

  selectLot(lot: HorizonMatrixLot): void {
    this.selectedLot = this.selectedLot?.unit_id === lot.unit_id ? null : lot;
  }

  lotAt(section: HorizonLevelSection, column: string): HorizonMatrixLot | undefined {
    return section.lots.find(lot => lot.column === column);
  }

  isSold(lot: HorizonMatrixLot): boolean {
    return lot.availability?.toLowerCase() === 'sold';
  }

  /** `$1.75M` for a million or more, `$850K` below it. */
  shortPrice(price: number): string {
    if (!price) {
      return '—';
    }
    return price >= 1_000_000 ? `$${(price / 1_000_000).toFixed(2).replace(/\.?0+$/, '')}M` : `$${Math.round(price / 1000)}K`;
  }

  lotTooltip(lot: HorizonMatrixLot): string {
    const e = lot.engagement;
    return `Lot ${lot.lot_name} · ${lot.availability} · ${e.views} views, ${e.unique_viewers} unique viewers, ${e.shortlists} shortlists`;
  }

  viewFullMatrix(): void {
    if (this.projectId) {
      void this.router.navigate(['/projects', 'edit_', this.projectId, 'price-list']);
    }
  }

  trackByBuilding(_index: number, building: { building_id: string }): string {
    return building.building_id;
  }

  trackByLevel(_index: number, section: HorizonLevelSection): string {
    return section.level;
  }

  trackByColumn(_index: number, column: string): string {
    return column;
  }
}
